import { Plus, Trash2 } from 'lucide-react';
import {
  GROUP_IMAGE_POSITIONS,
  SLIDE_IMAGE_ALIGNS,
  SLIDE_IMAGE_SIZES,
  type TrainingQuestionGroupDef,
} from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { ImageUploadInput } from './ImageUploadInput';
import { genId } from './moduleOps';
import { SortableList, SortableRow, DragHandle, reorder } from './SortableList';

/**
 * Editor for a question grid: the shared answer columns across the top, one
 * row per question, and an optional illustration placed around the grid.
 * Every row is a real question with its own points, which is why the correct
 * column is picked per row rather than per group.
 */
export function QuestionGroupEditor({
  group,
  onChange,
  graded = true,
}: {
  group: TrainingQuestionGroupDef;
  onChange: (next: TrainingQuestionGroupDef) => void;
  graded?: boolean;
}) {
  function setColumn(id: string, label: string) {
    onChange({ ...group, options: group.options.map((o) => (o.id === id ? { ...o, label } : o)) });
  }

  function removeColumn(id: string) {
    onChange({
      ...group,
      options: group.options.filter((o) => o.id !== id),
      // A row pointing at the removed column would fail publish validation
      // with a message about the row, not the column the author just deleted.
      rows: group.rows.map((r) =>
        r.correct_option_id === id ? { ...r, correct_option_id: undefined } : r,
      ),
    });
  }

  function setRow(id: string, patch: Partial<TrainingQuestionGroupDef['rows'][number]>) {
    onChange({ ...group, rows: group.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-64 flex-1 text-sm">
          <span className="font-medium text-fg-muted">Group heading</span>
          <input
            value={group.title ?? ''}
            onChange={(e) => onChange({ ...group, title: e.target.value })}
            placeholder="e.g. Keyboards"
            className={SHARED_INPUT_CLASS}
          />
        </label>
      </div>
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Intro (optional)</span>
        <textarea
          rows={2}
          value={group.intro ?? ''}
          onChange={(e) => onChange({ ...group, intro: e.target.value })}
          placeholder="A line of context above the questions"
          className={SHARED_INPUT_CLASS}
        />
      </label>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-fg-muted">
          Answer columns (shared by every question below)
        </legend>
        {group.options.map((o) => (
          <div key={o.id} className="flex items-center gap-2">
            <input
              value={o.label}
              onChange={(e) => setColumn(o.id, e.target.value)}
              placeholder="e.g. Yes"
              className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
            />
            <button
              type="button"
              aria-label="Remove column"
              disabled={group.options.length <= 2}
              onClick={() => removeColumn(o.id)}
              className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({ ...group, options: [...group.options, { id: genId('opt'), label: '' }] })
          }
        >
          Add column
        </Button>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-fg-muted">Questions</legend>
        <SortableList
          ids={group.rows.map((r) => r.id)}
          onReorder={(from, to) => onChange({ ...group, rows: reorder(group.rows, from, to) })}
          className="space-y-2"
        >
          {group.rows.map((row) => (
            <SortableRow
              key={row.id}
              id={row.id}
              className={(dragging) =>
                `rounded-token-sm border border-line/10 bg-surface p-2 ${
                  dragging ? 'shadow-token' : ''
                }`
              }
            >
              {({ dragHandle }) => (
                <div className="flex flex-wrap items-center gap-2">
                  <DragHandle dragHandle={dragHandle} label={`Reorder: ${row.prompt}`} />
                  <input
                    value={row.prompt}
                    onChange={(e) => setRow(row.id, { prompt: e.target.value })}
                    placeholder="e.g. Is your keyboard separate from the screen?"
                    className={`${SHARED_INPUT_CLASS} mt-0 min-w-48 flex-1`}
                  />
                  {graded ? (
                    <label className="text-xs text-fg-muted">
                      <span className="sr-only">Correct column</span>
                      <select
                        value={row.correct_option_id ?? ''}
                        onChange={(e) => setRow(row.id, { correct_option_id: e.target.value })}
                        className="rounded-token-sm border border-line-strong/20 bg-surface px-2 py-2 text-sm text-fg"
                      >
                        <option value="">Correct…</option>
                        {group.options.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label || 'Untitled'}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {graded ? (
                    <input
                      type="number"
                      min={1}
                      max={100}
                      aria-label="Points"
                      value={row.points}
                      onChange={(e) =>
                        setRow(row.id, {
                          points: Math.max(1, Math.floor(Number(e.target.value) || 1)),
                        })
                      }
                      className="w-20 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-2 text-sm"
                    />
                  ) : null}
                  <label className="flex items-center gap-1.5 text-xs text-fg-muted">
                    <input
                      type="checkbox"
                      checked={row.optional === true}
                      onChange={(e) => setRow(row.id, { optional: e.target.checked })}
                    />
                    Optional
                  </label>
                  <button
                    type="button"
                    aria-label="Remove question"
                    disabled={group.rows.length <= 1}
                    onClick={() =>
                      onChange({ ...group, rows: group.rows.filter((r) => r.id !== row.id) })
                    }
                    className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </SortableRow>
          ))}
        </SortableList>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({
              ...group,
              rows: [...group.rows, { id: genId('q'), prompt: '', points: 10 }],
            })
          }
        >
          Add question
        </Button>
      </fieldset>

      <div className="space-y-2 border-t border-line/10 pt-3">
        <ImageUploadInput
          value={group.image_key}
          onChange={(image_key) => onChange({ ...group, image_key })}
          label="Illustration (optional)"
        />
        {group.image_key ? (
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="font-medium text-fg-muted">Position</span>
              <select
                value={group.image_position ?? 'below'}
                onChange={(e) =>
                  onChange({
                    ...group,
                    image_position: e.target.value as TrainingQuestionGroupDef['image_position'],
                  })
                }
                className={SHARED_INPUT_CLASS}
              >
                {GROUP_IMAGE_POSITIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="font-medium text-fg-muted">Align</span>
              <select
                value={group.image_align ?? 'center'}
                onChange={(e) =>
                  onChange({
                    ...group,
                    image_align: e.target.value as TrainingQuestionGroupDef['image_align'],
                  })
                }
                className={SHARED_INPUT_CLASS}
              >
                {SLIDE_IMAGE_ALIGNS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="font-medium text-fg-muted">Size</span>
              <select
                value={group.image_size ?? 'medium'}
                onChange={(e) =>
                  onChange({
                    ...group,
                    image_size: e.target.value as TrainingQuestionGroupDef['image_size'],
                  })
                }
                className={SHARED_INPUT_CLASS}
              >
                {SLIDE_IMAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-48 flex-1 text-sm">
              <span className="font-medium text-fg-muted">Caption</span>
              <input
                value={group.image_caption ?? ''}
                onChange={(e) => onChange({ ...group, image_caption: e.target.value })}
                className={SHARED_INPUT_CLASS}
              />
            </label>
            <label className="min-w-48 flex-1 text-sm">
              <span className="font-medium text-fg-muted">Alt text</span>
              <input
                value={group.image_alt ?? ''}
                onChange={(e) => onChange({ ...group, image_alt: e.target.value })}
                placeholder="Describes the picture for screen readers"
                className={SHARED_INPUT_CLASS}
              />
            </label>
          </div>
        ) : null}
      </div>
    </div>
  );
}
